import { apiClient } from '@/shared/api';
import type {
  BehaviorFactorDetail,
  BehaviorFactorFormValues,
  BehaviorFactorsTreeParams,
  BehaviorFactorsTreeResponse,
  KnowledgeListParams,
  KnowledgeListResponse,
  KnowledgeNodeCreate,
  KnowledgeNodeCreateResponse,
  KnowledgeNodeResponse,
  KnowledgeNodeUpdate,
} from './types';

// Re-export types for convenience
export type {
  BehaviorFactorDetail,
  BehaviorFactorFormValues,
  BehaviorFactorsTreeParams,
  BehaviorFactorsTreeResponse,
  KnowledgeListParams,
  KnowledgeListResponse,
  KnowledgeNodeCreate,
  KnowledgeNodeCreateResponse,
  KnowledgeNodeResponse,
  KnowledgeNodeUpdate,
};

/**
 * Fetch a list of knowledge nodes per GET /knowledge
 */
export const fetchKnowledgeNodes = async (params?: KnowledgeListParams): Promise<KnowledgeListResponse> => {
  const { data } = await apiClient.get<KnowledgeListResponse>('/knowledge', { params });
  return data;
};

/**
 * Fetch a knowledge node by ID per GET /knowledge/{id}
 */
export const fetchKnowledgeNode = async (id: number | string): Promise<KnowledgeNodeResponse> => {
  const { data } = await apiClient.get<KnowledgeNodeResponse>(`/knowledge/${id}`);
  return data;
};

/**
 * Create a knowledge node per POST /knowledge
 */
export const createKnowledgeNode = async (payload: KnowledgeNodeCreate): Promise<KnowledgeNodeCreateResponse> => {
  const { data } = await apiClient.post<KnowledgeNodeCreateResponse>('/knowledge', payload);
  return data;
};

/**
 * Update a knowledge node per PUT /knowledge/{id}
 */
export const updateKnowledgeNode = async (
  id: number | string,
  payload: KnowledgeNodeUpdate,
): Promise<KnowledgeNodeResponse> => {
  const { data } = await apiClient.put<KnowledgeNodeResponse>(`/knowledge/${id}`, payload);
  return data;
};

/**
 * Delete a knowledge node per DELETE /knowledge/{id}
 */
export const deleteKnowledgeNode = async (id: number | string): Promise<void> => {
  await apiClient.delete(`/knowledge/${id}`);
};

/**
 * Fetch behavior factors tree per GET /knowledge/behavior-factors/tree
 */
export const fetchBehaviorFactorsTree = async (
  params?: BehaviorFactorsTreeParams,
): Promise<BehaviorFactorsTreeResponse> => {
  const { data } = await apiClient.get<BehaviorFactorsTreeResponse>('/knowledge/behavior-factors/tree', { params });
  return data;
};

/**
 * Fetch behavior factor detail per GET /knowledge/{id}
 */
export const fetchBehaviorFactor = async (id: number | string): Promise<BehaviorFactorDetail> => {
  const { data } = await apiClient.get<BehaviorFactorDetail>(`/knowledge/${id}`);
  return data;
};

/**
 * Create behavior factor per POST /knowledge
 */
export const createBehaviorFactor = async (payload: BehaviorFactorFormValues): Promise<KnowledgeNodeCreateResponse> => {
  const { data } = await apiClient.post<KnowledgeNodeCreateResponse>('/knowledge', payload);
  return data;
};

/**
 * Update behavior factor per PUT /knowledge/{id}
 */
export const updateBehaviorFactor = async (
  id: number | string,
  payload: BehaviorFactorFormValues,
): Promise<BehaviorFactorDetail> => {
  const { data } = await apiClient.put<BehaviorFactorDetail>(`/knowledge/${id}`, payload);
  return data;
};
